<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px"/>
  </div>

  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else>
    <div class="receipt-size-bar mx-3 mt-3 flex flex-row items-center gap-2">
      <el-radio-group dir="ltr" :model-value="receiptSize" size="small" @update:model-value="selectSize">
        <el-radio-button v-for="size in sizeOptions" :key="size" :value="size" :label="size">{{ size.toUpperCase() }}</el-radio-button>
        <el-radio-button v-if="cardPrintAvailable" value="card" label="card">{{ t('card') }}</el-radio-button>
      </el-radio-group>
      <el-radio-group v-if="canChoosePrintPart" v-model="printPart" dir="ltr" size="small" class="receipt-part-group">
        <el-radio-button v-for="part in orderedPrintParts" :key="part" :value="part" :label="part">{{ t(`printPart.${part}`) }}</el-radio-button>
      </el-radio-group>
    </div>
    <div class="flex flex-row">
      <div class="flex flex-col">
        <action-button :title="t('print')" faIcon="print" @click="print"/>
        <action-button :title="t('saveAsImage')" faIcon="image" @click="saveAsImage"/>
        <action-button v-if="!isRetinoscopy && settingsStore.getValue('show_qr_codes_on_eye_examination_reports') == 'true'" :title="t('saveQRCode')" iconify="mdi:qrcode" @click="saveQRCode"/>
      </div>

      <div
          v-if="receiptSize != 'a6'"
          id="examination-receipt"
          class="receipt-page-slot flex flex-col mt-3"
      >
      <div
          v-for="(page, pageIndex) in receiptPages"
          :key="page.kind"
          ref="receiptPageEls"
          class="receipt-page flex flex-col items-center mx-4 px-2 relative overflow-hidden a4-size"
          :class="{ 'a5-page': isA5, 'mt-3': pageIndex > 0, rxpad: rxPad }"
          :style="rxPad ? { '--rx': a6BrandingColor } : undefined"
          style="direction: ltr; overflow-x: hidden; font-size: 1.1rem"
      >
        <img class="watermark"
             :src="settingsStore.getValue('examination_watermark_image') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_watermark_image'): watermarkImage"
             :style="{right: settingsStore.getValue('examination_watermark_position_right') + 'px', top: settingsStore.getValue('examination_watermark_position_top') + 'px', width: settingsStore.getValue('examination_watermark_size') + 'px'}"
             alt=""/>
        <div v-if="rxPad" class="rxpad-bar"></div>
        <div v-if="rxPad" class="rxpad-mark">&#8478;</div>
        <div class="flex flex-col items-center w-full" style="">
          <!-- header info -->
          <div id="examination-receipt-header" class="erh-header w-full" :class="{ 'receipt-blank-area': page.blank }" :style="headerStyle">
            <div class="erh-band">
              <div class="erh-brand">
              <img
                  class="examination-main-logo"
                  :src="settingsStore.getValue('examination_main_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_main_logo'): samiLogo"
                  :style="{marginLeft: (Number(settingsStore.getValue('examination_main_logo_position_left')) || 0) + 'px', marginTop: (Number(settingsStore.getValue('examination_main_logo_position_top')) || 0) + 'px', width: settingsStore.getValue('examination_main_logo_size') ? settingsStore.getValue('examination_main_logo_size') + 'px' : undefined}"
                  alt=""/>
              </div>
              <div class="erh-title">
                <div v-if="settingText('examination_company_name')" class="erh-company">{{ settingText('examination_company_name') }}</div>
                <div v-if="page.kind === 'retinoscopy'" class="erh-title-main">Retinoscopy Report</div>
                <div v-else class="erh-title-main erh-title-ar" dir="rtl">تقرير فحص البصر</div>
                <div class="erh-ornament"><span></span><i></i><span></span></div>
                <div v-if="page.kind !== 'retinoscopy'" class="erh-title-sub">Eye Examination Report</div>
              </div>
              <div class="erh-aside">
                <div v-if="pageIndex === 0 && !isRetinoscopy && settingsStore.getValue('show_qr_codes_on_eye_examination_reports') == 'true'" class="examination-qr erh-qr">
                  <qrcode-vue
              :value="`https://exams.thecky.com/${settingsStore.getValue('viewer_client_key') || 'test'}/${$route.params.examination_id}`"
              :size="70"
              level="M"
              render-as="svg"
          />
                </div>
              </div>
            </div>
            <div class="erh-rule"></div>
          </div>

          <div id="examination-receipt-body" class="w-full px-8" :class="[page.kind === 'retinoscopy' || rxPad ? 'mt-3' : 'mt-8', { 'receipt-blank-area': page.blank }]">
            <div class="erh-patient" :class="patientClass" :dir="pageDir(page.kind)" :style="{ background: brandTint(0.06), borderColor: brandTint(0.35) }">
              <div class="erh-patient-name">
                <div class="erh-label">{{ pageTranslate(page.kind, 'Customer', 'receipts.customer') }}</div>
                <div class="erh-value erh-name erh-fit" :class="{ 'erh-name-long': nameIsLong }">{{ examination?.customer?.name ?? '' }}</div>
              </div>
              <div v-if="customerAge">
                <div class="erh-label">{{ pageTranslate(page.kind, 'Age', 'receipts.age') }}</div>
                <div class="erh-value">{{ customerAge }}</div>
              </div>
              <div>
                <div class="erh-label">{{ pageTranslate(page.kind, 'Exam No.', 'receipts.examNumber') }}</div>
                <div class="erh-value"><bdi dir="ltr">{{ examination?.externalID ?? '' }}</bdi></div>
              </div>
              <div>
                <div class="erh-label">{{ pageTranslate(page.kind, 'Exam Date', 'receipts.examDate') }}</div>
                <div class="erh-value"><bdi dir="ltr">{{ examDate }}</bdi></div>
              </div>
            </div>
          </div>

          <RetinoscopyReceiptBody
              v-if="page.kind === 'retinoscopy'"
              :hide-empty-sections="hideEmptySections"
              :retinoscopy="examination?.retinoscopy"
              :notes="retinoscopyNotes"
              :a5="isA5"
              :arabic="pageDir(page.kind) == 'rtl'"
              :branding-color="a6BrandingColor"
          />
          <EyeExamReceiptBody
              v-else
              :examination="examination"
              :size="isA5 ? 'a5' : 'a4'"
              :branding-color="a6BrandingColor"
              :arabic="pageDir(page.kind) == 'rtl'"
              :hide-empty-sections="hideEmptySections"
              :add-reading-to-distance="addReadingToDistance"
          />

          <div class="flex flex-col justify-center items-center self-center w-full">
            <div id="examination-receipt-footer" class="erh-footer w-full px-8 pt-4 pb-4">
              <div class="erh-signatures" :class="{ 'receipt-blank-area': page.blank }" :dir="pageDir(page.kind)">
                <div class="erh-sign">
                  <div class="erh-sign-name erh-fit">{{ examination?.examinator?.name ?? '' }}</div>
                  <div class="erh-sign-line"></div>
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Optometrist', 'receipts.optometrist') }}</div>
                </div>
                <div v-if="examination?.doctor" class="erh-sign">
                  <div class="erh-sign-name erh-fit">{{ examination?.doctor }}</div>
                  <div class="erh-sign-line"></div>
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Doctor', 'receipts.doctor') }}</div>
                </div>
                <div v-if="showInsurer" class="erh-sign">
                  <div class="erh-sign-name erh-fit">{{ examination?.insurance?.name }}</div>
                  <div class="erh-sign-line"></div>
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Insurer', 'receipts.insurance') }}</div>
                </div>
                <div v-if="nextAppointmentDate" class="erh-appt" :class="{ 'erh-appt-compact': compactAppointment }" :style="{ background: brandTint(0.12), borderColor: a6BrandingColor }">
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Next Appt.', 'receipts.nextAppt') }}</div>
                  <div class="erh-value ltr-numbers">{{ nextAppointmentDate }}</div>
                </div>
              </div>
              <div v-if="footerAddress || footerContactItems.length" class="erh-contact" :class="{ 'receipt-blank-area': page.blank }" :style="a6SectionHeaderStyle" :dir="footerDir">
                <div v-if="footerAddress" class="erh-contact-row"><bdi>{{ footerAddress }}</bdi></div>
                <div v-if="footerContactItems.length" class="erh-contact-row">
                  <span v-for="item in footerContactItems" :key="item" class="erh-contact-item"><bdi>{{ item }}</bdi></span>
                </div>
              </div>
              <div v-if="settingText('examination_footer_message')" class="erh-footer-message" :class="{ 'receipt-blank-area': page.blank }" :dir="footerDir">
                {{ settingText('examination_footer_message') }}
              </div>
              <div
                  v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
                  class="flex items-center justify-between pt-3 mt-2"
                  style="border-top: 1px solid #BDC7D6"
              >
                <img src="@/assets/images/thecky-qr.png" style="height: 25px;" alt=""/>
                <img src="@/assets/images/footer.svg" style="height: 25px;" alt=""/>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div v-else id="examination-receipt" class="receipt-page-slot mx-4 mt-3">
      <div
          v-for="(page, pageIndex) in receiptPages"
          :key="page.kind"
          class="receipt-page flex flex-col px-2 relative overflow-hidden a6-size"
          :class="{ 'mt-3': pageIndex > 0, rxpad: rxPad }"
          :style="rxPad ? { '--rx': a6BrandingColor } : undefined"
          style="direction: ltr; overflow-x: hidden"
      >
        <img class="watermark"
             :src="settingsStore.getValue('examination_watermark_image') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_watermark_image'): watermarkImage"
             :style="{right: settingsStore.getValue('examination_watermark_position_right') + 'px', top: settingsStore.getValue('examination_watermark_position_top') + 'px', width: settingsStore.getValue('examination_watermark_size') + 'px'}"
             alt=""/>
        <div v-if="rxPad" class="rxpad-bar"></div>
        <div v-if="rxPad" class="rxpad-mark">&#8478;</div>

        <div class="a6-content w-full h-full flex flex-col px-3 pt-3 pb-2">
          <div id="examination-receipt-header" class="erh-header w-full" :class="{ 'receipt-blank-area': false }" :style="headerStyle">
            <div class="erh-band">
              <div class="erh-brand"><img class="a6-header-logo" :src="settingsStore.getValue('examination_main_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_main_logo'): samiLogo" alt=""/></div>
              <div class="erh-title">
                <div v-if="settingText('examination_company_name')" class="erh-company">{{ settingText('examination_company_name') }}</div>
                <div v-if="page.kind === 'retinoscopy'" class="erh-title-main">Retinoscopy Report</div>
                <div v-else class="erh-title-main erh-title-ar" dir="rtl">تقرير فحص البصر</div>
                <div class="erh-ornament"><span></span><i></i><span></span></div>
                <div v-if="page.kind !== 'retinoscopy'" class="erh-title-sub">Eye Examination Report</div>
              </div>
              <div class="erh-aside">
              <qrcode-vue
                  v-if="pageIndex === 0 && !isRetinoscopy && settingsStore.getValue('show_qr_codes_on_eye_examination_reports') == 'true'"
                  class="erh-a6-qr"
                  :value="`https://exams.thecky.com/${settingsStore.getValue('viewer_client_key') || 'test'}/${$route.params.examination_id}`"
                  :size="40"
                  level="M"
                  render-as="svg"
              />
              </div>
            </div>
            <div class="erh-rule"></div>
          </div>

          <div class="erh-patient a6-patient-gap" :class="patientClass" :dir="pageDir(page.kind)" :style="{ background: brandTint(0.06), borderColor: brandTint(0.35) }">
              <div class="erh-patient-name">
                <div class="erh-label">{{ pageTranslate(page.kind, 'Customer', 'receipts.customer') }}</div>
                <div class="erh-value erh-name erh-fit" :class="{ 'erh-name-long': nameIsLong }">{{ examination?.customer?.name ?? '' }}</div>
              </div>
              <div v-if="customerAge">
                <div class="erh-label">{{ pageTranslate(page.kind, 'Age', 'receipts.age') }}</div>
                <div class="erh-value">{{ customerAge }}</div>
              </div>
              <div>
                <div class="erh-label">{{ pageTranslate(page.kind, 'Exam No.', 'receipts.examNumber') }}</div>
                <div class="erh-value"><bdi dir="ltr">{{ examination?.externalID ?? '' }}</bdi></div>
              </div>
              <div>
                <div class="erh-label">{{ pageTranslate(page.kind, 'Exam Date', 'receipts.examDate') }}</div>
                <div class="erh-value"><bdi dir="ltr">{{ examDate }}</bdi></div>
              </div>
            </div>

          <RetinoscopyReceiptBody
              v-if="page.kind === 'retinoscopy'"
              compact
              :hide-empty-sections="hideEmptySections"
              :retinoscopy="examination?.retinoscopy"
              :notes="retinoscopyNotes"
              :arabic="pageDir(page.kind) == 'rtl'"
              :branding-color="a6BrandingColor"
          />
          <EyeExamReceiptBody
              v-else
              :examination="examination"
              size="a6"
              :branding-color="a6BrandingColor"
              :arabic="pageDir(page.kind) == 'rtl'"
              :hide-empty-sections="hideEmptySections"
              :add-reading-to-distance="addReadingToDistance"
          />

          <div class="erh-footer mt-auto pt-2.5 w-full">
            <div class="erh-signatures" :class="{ 'receipt-blank-area': false }" :dir="pageDir(page.kind)">
                <div class="erh-sign">
                  <div class="erh-sign-name erh-fit">{{ examination?.examinator?.name ?? '' }}</div>
                  <div class="erh-sign-line"></div>
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Optometrist', 'receipts.optometrist') }}</div>
                </div>
                <div v-if="examination?.doctor" class="erh-sign">
                  <div class="erh-sign-name erh-fit">{{ examination?.doctor }}</div>
                  <div class="erh-sign-line"></div>
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Doctor', 'receipts.doctor') }}</div>
                </div>
                <div v-if="showInsurer" class="erh-sign">
                  <div class="erh-sign-name erh-fit">{{ examination?.insurance?.name }}</div>
                  <div class="erh-sign-line"></div>
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Insurer', 'receipts.insurance') }}</div>
                </div>
                <div v-if="nextAppointmentDate" class="erh-appt" :class="{ 'erh-appt-compact': compactAppointment }" :style="{ background: brandTint(0.12), borderColor: a6BrandingColor }">
                  <div class="erh-label">{{ pageTranslate(page.kind, 'Next Appt.', 'receipts.nextAppt') }}</div>
                  <div class="erh-value ltr-numbers">{{ nextAppointmentDate }}</div>
                </div>
              </div>
              <div v-if="footerAddress || footerContactItems.length" class="erh-contact" :class="{ 'receipt-blank-area': false }" :style="a6SectionHeaderStyle" :dir="footerDir">
                <div v-if="footerAddress" class="erh-contact-row"><bdi>{{ footerAddress }}</bdi></div>
                <div v-if="footerContactItems.length" class="erh-contact-row">
                  <span v-for="item in footerContactItems" :key="item" class="erh-contact-item"><bdi>{{ item }}</bdi></span>
                </div>
              </div>
              <div v-if="settingText('examination_footer_message')" class="erh-footer-message" :class="{ 'receipt-blank-area': false }" :dir="footerDir">
                {{ settingText('examination_footer_message') }}
              </div>
            <div
                v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
                class="a6-thecky-footer flex items-center justify-between mt-1 px-1"
                style="border-top: 1px solid #BDC7D6; padding-top: 3px"
                dir="ltr"
            >
              <img src="@/assets/images/thecky-qr.png" class="a6-thecky-qr" alt=""/>
              <img src="@/assets/images/footer.svg" class="a6-thecky-logo" alt=""/>
            </div>
          </div>
        </div>
      </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import Examination, {
  EXAM_TYPE_EYE_EXAM,
  EXAM_TYPE_RETINOSCOPY,
  type ExamType,
  examinationI18nMessages,
  eyeExamHasValues,
  normalizeRetinoscopy,
  retinoscopyIsFilled
} from '@/models/customer/Examination'
import {formatAge, formatPostgresDate, parseFormat} from '@/utils/date'
import ActionButton from '@/components/ActionButton.vue'
import {useSettingsStore} from '@/stores/settingsStore'
import html2canvas from 'html2canvas'
import QrcodeVue from 'qrcode.vue'

import samiLogo from '@/assets/images/logo.svg'
import watermarkImage from '@/assets/images/watermark.png'
import {twMerge} from "tailwind-merge";
import {exportQRCode} from '@/utils/qrExport'
import RetinoscopyReceiptBody from './RetinoscopyReceiptBody.vue'
import EyeExamReceiptBody from './EyeExamReceiptBody.vue'

const ARABIC_TEXT = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/

export default defineComponent({
  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton,
    QrcodeVue,
    RetinoscopyReceiptBody,
    EyeExamReceiptBody
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...examinationI18nMessages.en,
          print: 'Print',
          saveAsImage: 'Save as Image',
          saveQRCode: 'Save QR Code',
          card: 'Card',
          eyeExam: 'Eye Exam',
          retinoscopy: 'Retinoscopy',
          printPart: {
            both: 'Both',
            eye: 'Eye Exam',
            retinoscopy: 'Retinoscopy',
          },
        },
        ar: {
          ...examinationI18nMessages.ar,
          print: 'اطبع',
          saveAsImage: 'حفظ كصورة',
          saveQRCode: 'حفظ رمز QR',
          card: 'Card',
          eyeExam: 'فحص العيون',
          retinoscopy: 'Retinoscopy',
          printPart: {
            both: 'الفحصين',
            eye: 'فحص العيون',
            retinoscopy: 'Retinoscopy',
          },
          receipts: {
            customer: 'الزبون',
            examNumber: 'رقم الفحص',
            optometrist: 'فاحص البصر',
            doctor: 'الطبيب',
            insurance: 'شركة التأمين',
            dateOfBirth: 'تاريخ الميلاد',
            age: 'العمر',
            examDate: 'تاريخ الفحص',
            nextAppt: 'الموعد القادم',
          }
        }
      }
    })

    const settingsStore = useSettingsStore();

    function renderExaminationNumber(
        value: number | undefined,
        withoutMinus = false,
        withoutPrecision = false
    ): string {
      if (value == undefined) {
        return ''
      }

      if (withoutMinus && withoutPrecision) {
        return `${value}`
      }

      return value > 0 ? `${withoutMinus ? '': '+'}${value.toFixed(2)}` : `${value.toFixed(2)}`
    }

    return {
      t,
      parseFormat,
      formatPostgresDate,
      renderExaminationNumber,
      settingsStore,
      samiLogo,
      watermarkImage
    }
  },
  data() {
    return {
      originalTitle: document.title,
      // The Card page links back with ?size=
      sizeOverride: (['a4', 'a5', 'a6'].includes(String(this.$route.query.size)) ? this.$route.query.size : '') as '' | 'a4' | 'a5' | 'a6',
      examination: null as Examination | null,
      // Exams with both an Eye Exam and a Retinoscopy can print either one or both
      printPart: 'both' as 'both' | 'eye' | 'retinoscopy',
      printPartOptions: ['both', 'eye', 'retinoscopy'] as const,
      loading: true,
      error: {
        title: '',
        body: ''
      }
    }
  },
  computed: {
    // Old stand-alone Retinoscopy exam
    isRetinoscopy(): boolean {
      return this.examination?.examType === EXAM_TYPE_RETINOSCOPY
    },
    // An Eye Exam that includes a Retinoscopy prints it on its own page right after the Eye Exam
    hasRetinoscopyPage(): boolean {
      return this.isRetinoscopy
          || (!!this.examination?.withRetinoscopy && retinoscopyIsFilled(normalizeRetinoscopy(this.examination?.retinoscopy)))
    },
    sizeOptions(): string[] {
      return ['a4', 'a5', 'a6']
    },
    cardPrintAvailable(): boolean {
      return !this.isRetinoscopy && this.settingsStore.getValue('hide_examination_id_print') == 'false'
    },
    receiptSize(): string {
      if (this.sizeOverride && this.sizeOptions.includes(this.sizeOverride)) return this.sizeOverride
      // One setting for both the Eye Exam and Retinoscopy reports
      const size = this.settingsStore.getValue('examination_receipt_size')
      return size == 'a5' || size == 'a6' ? size : 'a4'
    },
    isA5(): boolean {
      return this.receiptSize == 'a5'
    },
    hideEmptySections(): boolean {
      return this.settingsStore.getValue('examination_print_sections') !== 'all'
    },
    rxPad(): boolean {
      return this.settingsStore.getValue('examination_receipt_design') == 'rx_pad'
    },
    addReadingToDistance(): boolean {
      return this.settingsStore.getValue('adding_reading_glasses_degrees_to_distances_when_printing') == 'true'
    },
    // The bar is forced LTR for its borders, so Arabic reverses the order to read right-to-left
    orderedPrintParts(): readonly ('both' | 'eye' | 'retinoscopy')[] {
      return this.$ctx.locale === 'ar' ? [...this.printPartOptions].reverse() : this.printPartOptions
    },
    canChoosePrintPart(): boolean {
      return !this.isRetinoscopy && this.hasRetinoscopyPage && eyeExamHasValues(this.examination)
    },
    // Each part prints on its own single page, in the chosen paper size
    receiptPages(): { kind: 'eye' | 'retinoscopy', blank: boolean }[] {
      if (this.isRetinoscopy) return [{kind: 'retinoscopy', blank: false}]
      const pages: { kind: 'eye' | 'retinoscopy', blank: boolean }[] = [{kind: 'eye', blank: false}]
      if (!this.hasRetinoscopyPage) return pages
      // Nothing entered in the Eye Exam: print the Retinoscopy page only
      if (!eyeExamHasValues(this.examination)) return [{kind: 'retinoscopy', blank: false}]
      if (this.printPart === 'eye') return pages
      if (this.printPart === 'retinoscopy') return [{kind: 'retinoscopy', blank: false}]
      return [...pages, {kind: 'retinoscopy', blank: false}]
    },
    // Stand-alone Retinoscopy exams kept their notes in the exam notes
    retinoscopyNotes(): string {
      return (this.isRetinoscopy ? this.examination?.notes : this.examination?.retinoscopy?.notes) || ''
    },
    // Retinoscopy reports are English-only
    receiptDir(): string {
      return this.isRetinoscopy || this.$ctx.receiptLanguage == 'en' ? 'ltr' : 'rtl'
    },
    // Names longer than this may wrap onto a second line; shorter ones shrink to stay on one line
    nameIsLong(): boolean {
      return (this.examination?.customer?.name ?? '').trim().length > 35
    },
    showInsurer(): boolean {
      return !!this.examination?.insurance?.name && this.settingsStore.getValue('show_insurance_companies_button_on_companies_page') == 'true'
    },
    compactAppointment(): boolean {
      return !!this.examination?.doctor && this.showInsurer
    },
    patientClass(): string {
      return this.receiptSize == 'a6' ? 'erh-patient-a6' : ''
    },
    customerAge(): string {
      const dob = this.examination?.customer?.dateOfBirth
      return dob && String(dob) != '0001-01-01T00:00:00Z' ? formatAge(String(dob)) : ''
    },
    examDate(): string {
      return formatPostgresDate(String(this.examination?.createdAt), 'DD-MM-YYYY') ?? ''
    },
    nextAppointmentDate(): string {
      if (!this.examination?.nextAppointment) return ''
      return formatPostgresDate(String(this.examination.nextAppointment), 'DD-MM-YYYY') ?? ''
    },
    // Footer text follows the receipt language even on English-only Retinoscopy reports
    footerDir(): string {
      return this.$ctx.receiptLanguage == 'en' ? 'ltr' : 'rtl'
    },
    footerAddress(): string {
      return this.settingText('company_address')
    },
    footerContactItems(): string[] {
      return [
        this.companyPhones,
        this.settingText('company_email'),
        this.settingText('company_website')
      ].filter(Boolean)
    },
    companyPhones(): string {
      return [this.settingText('company_phone1'), this.settingText('company_phone2')].filter(Boolean).join(' - ')
    },
    headerStyle(): Record<string, string> {
      return {'--brand': this.a6BrandingColor, '--brand-soft': this.brandTint(0.35)}
    },
    a6BrandingColor(): string {
      return this.settingsStore.getValue('receipt_branding_color') || '#14919B'
    },
    a6SectionHeaderStyle(): Record<string, string> {
      const match = /^#?([0-9a-f]{6})$/i.exec(this.a6BrandingColor.trim())
      const n = match ? parseInt(match[1], 16) : 0x14919B
      const luminance = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
      return {
        background: this.a6BrandingColor,
        color: luminance > 0.65 ? '#3a3a3a' : '#fff',
      }
    },
  },
  beforeMount() {
    this.loadExamination()
  },
  updated() {
    this.$nextTick(() => this.fitTexts())
  },
  created() {
    // Add print CSS when component is created
    this.addPrintCSS()
  },
  mounted() {
    document.fonts?.ready.then(() => this.fitTexts())
    window.addEventListener('beforeprint', this.fitRetinoscopyPages)
  },
  watch: {
    '$route.params.examination_id'(id: string, oldID: string) {
      if (!id || id === oldID || this.$route.name !== 'examination-receipt') return
      this.loading = true
      this.sizeOverride = ''
      this.printPart = 'both'
      this.loadExamination()
    },
    printPart() {
      this.$nextTick(() => {
        this.fitTexts()
        this.fitRetinoscopyPages()
      })
    },
    isRetinoscopy() {
      this.sizeOverride = ''
      this.removePrintCSS()
      this.addPrintCSS()
      this.$nextTick(() => {
        this.fitTexts()
        this.fitRetinoscopyPages()
      })
    },
    receiptSize() {
      this.removePrintCSS()
      this.addPrintCSS()
      this.$nextTick(() => {
        this.fitTexts()
        this.fitRetinoscopyPages()
      })
    },
  },
  beforeUnmount() {
    window.removeEventListener('beforeprint', this.fitRetinoscopyPages)
    this.removePrintCSS()
    document.title = this.originalTitle
  },
  methods: {
    async loadExamination() {
      if (this.$route.params.examination_id) {
        // get order
        try {
          const resp = await this.$http.get<Examination>(
              `${Examination.ENDPOINT}/${this.$route.params.examination_id}`
          )
          this.examination = Examination.from(resp)
          this.removePrintCSS()
          this.addPrintCSS()
          if (this.hasRetinoscopyPage) {
            this.$nextTick(this.fitRetinoscopyPages)
            document.fonts?.ready.then(() => this.fitRetinoscopyPages())
          }
          document.title = `Thecky ${this.examination.externalID} | ${formatPostgresDate(String(this.examination.createdAt), 'DD-MM-YYYY')}`
        } catch (error) {
          this.error.title = error.title
          this.error.body = error.body
        } finally {
          this.loading = false
        }
      }
    },
    // Long notes would run into the footer: shrink the notes text first, then the whole exam body
    selectSize(size: string | number | boolean | undefined) {
      if (size === 'card') {
        this.$router.replace({ name: 'examination-id-print', params: { examination_id: this.examination?.id } })
        return
      }
      this.sizeOverride = size as 'a4' | 'a5' | 'a6'
    },
    fitRetinoscopyPages() {
      if (!this.hasRetinoscopyPage) return
      const pages = (this.$refs.receiptPageEls || []) as HTMLElement[]
      for (const page of pages) {
        const body = page.querySelector<HTMLElement>('.retinoscopy-receipt')
        const footer = page.querySelector<HTMLElement>('#examination-receipt-footer')
        if (!body || !footer) continue
        const hasNotes = !!body.querySelector('.rr-notes')
        // A5 notes are printed larger and should stay readable
        const minNotesScale = this.isA5 ? 0.75 : 0.6
        let notesScale = 1
        let bodyScale = 1
        const apply = () => {
          page.style.setProperty('--rr-notes-scale', String(notesScale))
          page.style.setProperty('--rr-body-scale', String(bodyScale))
        }
        apply()
        for (let i = 0; i < 40; i++) {
          const pageRect = page.getBoundingClientRect()
          const zoom = page.offsetHeight ? pageRect.height / page.offsetHeight : 1
          const limit = pageRect.bottom - footer.getBoundingClientRect().height - 20 * zoom
          if (body.getBoundingClientRect().bottom <= limit) break
          if (hasNotes && notesScale > minNotesScale) notesScale = Math.round((notesScale - 0.05) * 100) / 100
          else if (bodyScale > 0.8) bodyScale = Math.round((bodyScale - 0.05) * 100) / 100
          else break
          apply()
        }
      }
    },
    formatAge,
    twMerge,
    print() {
      window.print()
    },
    // Shrinks names until they fit their box: one line, or two lines for long customer names
    fitTexts() {
      const root = this.$el as HTMLElement | undefined
      if (!root?.querySelectorAll) return
      root.querySelectorAll<HTMLElement>('.erh-name').forEach(el => {
        this.fitGroup([el], el.classList.contains('erh-name-long') ? 2 : 1, 0.45)
      })
      // Signature names share one size so they look even
      this.fitGroup([...root.querySelectorAll<HTMLElement>('.erh-sign-name')], 1, this.receiptSize == 'a6' ? 0.7 : 0.55)
    },
    fitGroup(els: HTMLElement[], lines: number, minScale: number) {
      if (!els.length) return
      els.forEach(el => {
        el.style.fontSize = ''
        el.style.whiteSpace = ''
      })
      const base = parseFloat(getComputedStyle(els[0]).fontSize)
      const overflows = (el: HTMLElement, size: number) => lines > 1
          ? el.scrollHeight > size * 1.3 * lines + 1
          : el.scrollWidth > el.clientWidth
      let size = base
      while (els.some(el => overflows(el, size)) && size > base * minScale) {
        size -= 0.25
        els.forEach(el => el.style.fontSize = `${size}px`)
      }
      // Still too long at the smallest size: wrap instead of cutting the name off
      els.forEach(el => {
        if (overflows(el, size)) el.style.whiteSpace = 'normal'
      })
    },
    // Settings saved empty can come back as the literal string "null"
    settingText(key: string): string {
      const value = this.settingsStore.getValue(key)
      return value == null || value === 'null' ? '' : String(value)
    },
    brandTint(alpha: number): string {
      const match = /^#?([0-9a-f]{6})$/i.exec(this.a6BrandingColor.trim())
      const n = match ? parseInt(match[1], 16) : 0x14919B
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    },
    examinationValue(field: string): string {
      const value = (this.examination as unknown as Record<string, unknown> | null)?.[field]
      return value == null ? '' : String(value)
    },
    // Header and footer follow the receipt language on every page; the Retinoscopy body itself stays English
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pageDir(kind: 'eye' | 'retinoscopy'): string {
      return this.$ctx.receiptLanguage == 'en' ? 'ltr' : 'rtl'
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pageTranslate(kind: 'eye' | 'retinoscopy', value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == 'en' ? value : this.t(translateKey, this.$ctx.receiptLanguage)
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.isRetinoscopy || this.$ctx.receiptLanguage == "en"
          ? value
          : this.t(translateKey, this.$ctx.receiptLanguage);
    },
    addPrintCSS() {
      // Create a style element with a unique id
      const style = document.createElement('style')
      style.setAttribute('id', 'print-css')
      if (this.isA5) {
        style.textContent = `
      @media print {
        @page {
          size: A5;
          margin: 0;
        }
        html, body {
          background: #fff !important;
        }
        #examination-receipt-footer {
          bottom: 0;
          position: absolute;
        }
        .a5-page {
          margin: 0 !important;
          break-after: page;
        }
        .a5-page:last-child {
          break-after: auto;
        }
      }`
      } else if (this.receiptSize == 'a4') {
        style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0;
        }
        .receipt-page {
          margin: 0 !important;
          break-after: page;
        }
        .receipt-page:last-child {
          break-after: auto;
        }
        #examination-receipt-footer {
          bottom: 0;
          position: absolute;
        }
      }`
      } else if (this.receiptSize == 'a6') {
        style.textContent = `
      @media print {
        @page {
          size: 105mm 148mm;
          margin: 0;
        }
        .receipt-page {
          margin: 0 !important;
          break-after: page;
        }
        .receipt-page:last-child {
          break-after: auto;
        }
      }`
      }

      // Append the style to the document head
      document.head.appendChild(style)
    },
    removePrintCSS() {
      // Find and remove the style element
      const style = document.querySelector('#print-css')
      if (style && style.parentNode) {
        style.parentNode.removeChild(style)
      }
    },
    async fetchImageBlob(src: string): Promise<Blob> {
      try {
        const resp = await fetch(src)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        return await resp.blob()
      } catch (e) {
        // Storage origin may not send CORS headers — load it through the API instead
        const storagePrefix = this.settingsStore.storagePath + '/'
        if (!src.startsWith(storagePrefix)) throw e
        const filePath = decodeURIComponent(src.slice(storagePrefix.length))
        return await this.$http.get<Blob>(`/storage/files/${encodeURIComponent(filePath)}`, {
          responseType: 'blob'
        })
      }
    },
    async saveAsImage() {
      try {
        const element = document.getElementById('examination-receipt')
        if (!element) {
          throw new Error('Receipt element not found')
        }

        // Convert images to data URLs (fixes SVG + cross-origin issues)
        const images = element.querySelectorAll('img')
        const originalSrcs = new Map<HTMLImageElement, string>()

        for (const img of Array.from(images)) {
          if (!img.src || img.src.startsWith('data:')) continue
          try {
            // Try canvas approach first (works for same-origin / local SVGs)
            let w = img.naturalWidth
            let h = img.naturalHeight
            if (w === 0 || h === 0) {
              const rect = img.getBoundingClientRect()
              w = Math.ceil(rect.width) || 100
              h = Math.ceil(rect.height) || 100
            }
            const c = document.createElement('canvas')
            c.width = w
            c.height = h
            c.getContext('2d')!.drawImage(img, 0, 0, w, h)
            originalSrcs.set(img, img.src)
            img.src = c.toDataURL('image/png')
          } catch (e) {
            // Canvas tainted (cross-origin) — fetch as blob instead
            try {
              const blob = await this.fetchImageBlob(img.src)
              const dataUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result as string)
                reader.readAsDataURL(blob)
              })
              originalSrcs.set(img, img.src)
              img.src = dataUrl
            } catch (_) {
              // Skip images that can't be converted at all
            }
          }
        }

        // Wait for images to update
        await new Promise(r => setTimeout(r, 100))

        // Capture an off-screen copy at real paper size (no preview zoom) so the visible page doesn't jump
        const clone = element.cloneNode(true) as HTMLElement
        clone.removeAttribute('id')
        const captureEls = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('.receipt-page'))]
        captureEls.forEach(el => {
          el.classList.remove('overflow-hidden')
          el.style.setProperty('zoom', '1', 'important')
          el.style.setProperty('overflow', 'visible', 'important')
          el.style.setProperty('overflow-x', 'visible', 'important')
        })
        const holder = document.createElement('div')
        holder.style.cssText = 'position: fixed; left: -100000px; top: 0; pointer-events: none;'
        holder.appendChild(clone)
        ;(element.parentElement ?? document.body).appendChild(holder)
        // html2canvas draws letter-spaced text one glyph at a time, which splits joined Arabic letters
        clone.querySelectorAll<HTMLElement>('*').forEach(el => {
          const ownText = Array.from(el.childNodes).some(n => n.nodeType === Node.TEXT_NODE && ARABIC_TEXT.test(n.textContent ?? ''))
          if (ownText && parseFloat(getComputedStyle(el).letterSpacing)) el.style.letterSpacing = '0'
        })

        let canvas: HTMLCanvasElement
        try {
          await new Promise(r => setTimeout(r, 50))
          canvas = await html2canvas(clone, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
          })
        } finally {
          holder.remove()
        }

        // Restore original image srcs
        for (const [img, src] of originalSrcs) {
          img.src = src
        }

        const dataUrl = canvas.toDataURL('image/png')
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `${this.examination?.customer?.name}-${this.examination?.externalID || 'unknown'}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (err) {
        const error = err as Error
        this.error.title = 'Error saving image'
        this.error.body = error.message || 'An unknown error occurred'
      }
    },
    async saveQRCode() {
      try {
        const logoSrc = this.settingsStore.getValue('examination_main_logo')
          ? this.settingsStore.storagePath + '/' + this.settingsStore.getValue('examination_main_logo')
          : this.samiLogo
        const qrUrl = `https://exams.thecky.com/${this.settingsStore.getValue('viewer_client_key') || 'test'}/${this.$route.params.examination_id}`
        await exportQRCode({
          qrUrl,
          logoSrc,
          customerName: this.examination?.customer?.name,
          externalID: this.examination?.externalID,
          examDate: this.examination?.createdAt ? formatPostgresDate(String(this.examination.createdAt), 'DD-MM-YYYY') : '',
          brandColor: this.settingsStore.getValue('receipt_branding_color'),
          style: this.settingsStore.getValue('examination_qr_image_style')
        })
      } catch (err) {
        const error = err as Error
        this.error.title = 'Error saving QR code'
        this.error.body = error.message || 'An unknown error occurred'
      }
    }
  }
})
</script>
<style>
/* The Arabic theme (rtlcss) mirrors radio-button borders; keep the size bar drawn LTR */
.receipt-size-bar .el-radio-button__inner {
  border-left: 0;
  border-right: var(--el-border);
  border-radius: 0;
}

.receipt-size-bar .el-radio-button:first-child .el-radio-button__inner {
  border-left: var(--el-border);
  border-radius: var(--el-border-radius-base) 0 0 var(--el-border-radius-base);
}

.receipt-size-bar .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 var(--el-border-radius-base) var(--el-border-radius-base) 0;
}

.receipt-size-bar .el-radio-button.is-active .el-radio-button__inner {
  border-color: var(--el-radio-button-checked-border-color, var(--el-color-primary));
  box-shadow: -1px 0 0 0 var(--el-radio-button-checked-border-color, var(--el-color-primary));
}

.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

.a4-size {
  width: 210mm !important;
  height: 297mm;
  overflow: hidden;
}

.examination-main-logo {
  height: auto;
  width: 120px; /* default when examination_main_logo_size is unset */
}

/* Keeps the header/footer space on A5 retinoscopy page 2 but prints it empty */
.receipt-blank-area,
.receipt-blank-area * {
  visibility: hidden !important;
}

/* A5 pages reuse the A4 layout scaled to A5 (148 x 210 mm) */
.a5-page {
  zoom: 0.7;
}

.a5-size {
  width: 148mm !important;
  height: 210mm;
}

@media print {
  .receipt-page-slot {
    margin: 0 !important;
  }
}

.a6-size {
  width: 105mm !important;
  height: 148mm;
  font-size: 9px;
}

/* On screen the A6 preview is scaled a little (still smaller than the A5 preview); print stays true A6.
   The top gap lives on the unzoomed .receipt-page-slot so pages line up with the Print button in every engine */
@media screen {
  .a6-size {
    zoom: 1.15;
  }
}

.a6-header-logo {
  height: 34px;
  width: auto;
  max-width: 110px;
  object-fit: contain;
}

.erh-header {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.erh-band {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 2rem 0.85rem;
}

.erh-brand {
  justify-self: start;
  display: flex;
  align-items: center;
}

.erh-aside {
  justify-self: end;
  display: flex;
  align-items: center;
}

.erh-aside .erh-qr {
  position: static;
  transform: none;
}

.erh-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.erh-company {
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--brand);
  margin-bottom: 0.15rem;
}

.erh-title-main {
  font-size: 1.5em;
  font-weight: 700;
  color: #1f1f1f;
  line-height: 1.25;
}

.erh-title-main.erh-title-ar {
  font-size: 1.15em;
}

.erh-ornament {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.3rem 0 0.3rem;
  color: var(--brand);
}

.erh-ornament span {
  width: 2.8rem;
  height: 1px;
  background: currentColor;
  opacity: 0.55;
}

.erh-ornament i {
  width: 5px;
  height: 5px;
  background: currentColor;
  transform: rotate(45deg);
}

.erh-title-sub {
  font-size: 0.66em;
  font-weight: 600;
  color: #6b6b6b;
  letter-spacing: 0.32em;
  padding-left: 0.32em;
  text-transform: uppercase;
}

.erh-rule {
  position: relative;
  margin: 0 2rem;
  border-top: 2px solid var(--brand);
}

.erh-rule::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 3px;
  border-top: 1px solid var(--brand-soft);
}

.erh-patient {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-columns: auto;
  gap: 0.5rem 1.75rem;
  padding: 0.55rem 0.9rem;
  border: 1px solid;
  border-radius: 8px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.erh-label {
  font-size: 0.68em;
  font-weight: 700;
  color: #8a8a8a;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.3;
}

/* Letter-spacing breaks Arabic letter joining (Safari / iPad print) */
[dir="rtl"] .erh-label,
[dir="rtl"].erh-contact,
[dir="rtl"].erh-footer-message {
  letter-spacing: 0;
}

.erh-value {
  font-weight: 700;
  color: #111;
  line-height: 1.3;
}

.erh-name {
  font-size: 1.2em;
}

.erh-fit {
  white-space: nowrap;
  overflow: hidden;
}

.erh-fit.erh-name-long {
  white-space: normal;
}

.erh-signatures {
  display: flex;
  align-items: flex-end;
  gap: 1.5rem;
  margin-bottom: 0.75rem;
}

.erh-sign {
  flex: 0 1 auto;
  min-width: 9rem;
  text-align: center;
}

.erh-sign-name {
  font-weight: 700;
  text-transform: capitalize;
  min-height: 1.3em;
}

.erh-sign-line {
  border-top: 1px dashed #9a9a9a;
  margin: 0.15rem 0;
}

.erh-appt {
  flex: none;
  margin-inline-start: auto;
  border: 1px solid;
  border-radius: 6px;
  padding: 0.25rem 0.75rem;
  text-align: center;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.erh-contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8em;
  font-weight: 800;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.erh-contact,
.erh-contact * {
  font-weight: 700 !important;
}

.erh-contact-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
}

.erh-contact-item + .erh-contact-item::before {
  content: '•';
  margin: 0 0.6rem;
  opacity: 0.6;
}

.erh-footer-message {
  text-align: center;
  font-size: 0.8em;
  color: #6e6e6e;
  margin-top: 0.35rem;
}

/* A6 uses the same header/footer at a compact scale */
.a6-size .erh-band {
  padding: 0 0 5px;
  gap: 6px;
}

.a6-size .erh-ornament {
  gap: 3px;
  margin: 2px 0;
}

.a6-size .erh-ornament span {
  width: 18px;
}

.a6-size .erh-ornament i {
  width: 3px;
  height: 3px;
}

.a6-size .erh-title-main {
  font-size: 12px;
}

.a6-size .erh-title-main.erh-title-ar {
  font-size: 9.5px;
}

.a6-size .erh-title-sub {
  font-size: 5.5px;
  letter-spacing: 0.26em;
}

.a6-size .erh-company {
  font-size: 6px;
  margin-bottom: 1px;
}

.a6-size .a6-patient-gap {
  margin-top: 2.5mm;
}

.a6-size .erh-rule {
  margin: 0;
  border-top-width: 1.5px;
}

.a6-size .erh-rule::after {
  top: 2px;
}

.erh-a6-qr {
  flex-shrink: 0;
}

.erh-patient-a6 {
  margin-top: 6px;
  padding: 5px 8px;
  gap: 2px 10px;
  border-radius: 4px;
}

.erh-patient-a6 .erh-label {
  font-size: 6.5px;
}

.erh-patient-a6 .erh-value {
  font-size: 10px;
}

.erh-patient-a6 .erh-name {
  font-size: 14px;
}

.a6-size .erh-signatures {
  gap: 8px;
  margin-bottom: 4px;
}

.a6-size .erh-sign {
  min-width: 16mm;
}

.a6-size .erh-sign-name {
  font-size: 8px;
}

.a6-size .erh-label {
  font-size: 6px;
}

.a6-size .erh-appt {
  padding: 1px 5px;
  border-radius: 3px;
}

.a6-size .erh-appt-compact .erh-label,
.a6-size .erh-appt-compact .erh-value {
  white-space: nowrap;
}

.a6-size .erh-appt-compact .erh-label {
  font-size: 5px;
  letter-spacing: 0.02em;
}

.a6-size .erh-appt-compact .erh-value {
  font-size: 8px;
}

.a6-size .erh-contact {
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 6.5px;
}

.a6-size .erh-contact {
  gap: 1px;
}

.a6-size .erh-contact-item + .erh-contact-item::before {
  margin: 0 4px;
}

.a6-size .erh-footer-message {
  font-size: 6.5px;
  margin-top: 2px;
}

.a6-thecky-qr,
.a6-thecky-logo {
  height: 20px;
}

#examination-receipt {
  overflow: scroll;
  overflow-x: hidden;
  background-color: #fff;
}

@media print {
  body * {
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #examination-receipt,
  #examination-receipt * {
    visibility: visible;
  }

  #examination-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0cm;
  }

  #examination-receipt-header {
    top: 0;
  }

  #examination-receipt-body {
    position: relative;
  }

  p,
  div {
    page-break-inside: avoid;
  }
}

.watermark {
  position: absolute;
}

.examination-qr {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.examination-input {
  min-width: 100px;
  min-height: 32px;
}

.examination-grid {
  display: grid;
  grid-template-columns: 180px 40px 30px 100px 100px 100px 100px;
  row-gap: 8px;
}

.examination-input-2 {
  min-width: 60px;
  min-height: 32px;
}

.font-bold {
  font-weight: 500 !important;
}
/* Rx Pad design (Settings: examination_receipt_design) */
.receipt-page.rxpad .rxpad-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 7px;
  background: var(--rx);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.receipt-page.rxpad .rxpad-mark {
  position: absolute;
  right: 4%;
  top: 22%;
  font-size: 300px;
  font-weight: 700;
  line-height: 1;
  color: var(--rx);
  opacity: 0.05;
  pointer-events: none;
}

/* Rx Pad signatures: small label above the name, no signature line */
.receipt-page.rxpad .erh-sign {
  display: flex;
  flex-direction: column-reverse;
  text-align: start;
  min-width: 0;
}

.receipt-page.rxpad .erh-sign-line {
  display: none;
}

.receipt-page.rxpad .erh-footer {
  padding-top: 0.25rem;
}

.receipt-page.rxpad .erh-signatures {
  margin-bottom: 0.25rem;
}

.receipt-page.rxpad .erh-sign-name {
  min-height: 0;
}

.receipt-page.rxpad.a6-size .rxpad-mark {
  font-size: 150px;
}

.receipt-page.rxpad .erh-band {
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: end;
  padding-top: 1.1rem;
  padding-bottom: 0.4rem;
}

.receipt-page.rxpad .erh-title {
  justify-self: end;
  align-items: flex-end;
  text-align: right;
}

.receipt-page.rxpad .erh-ornament {
  display: none;
}

.receipt-page.rxpad .erh-title-main.erh-title-ar {
  font-size: 1.25em;
}

.receipt-page.rxpad .erh-rule {
  border-top: 3px double var(--rx);
}

.receipt-page.rxpad .erh-rule::after {
  display: none;
}

.receipt-page.rxpad .erh-patient {
  background: transparent !important;
  border: 0 !important;
  border-bottom: 3px double var(--rx) !important;
  border-radius: 0;
  padding: 0.35rem 0.1rem 0.4rem;
}

.receipt-page.rxpad .rr-tall .rr-cross {
  height: 13em;
}

/* A5: smaller crosses leave room for the notes */
.receipt-page .rr-tall.rr-a5 .rr-cross {
  height: 12em;
}

.receipt-page .rr-tall.rr-a5 .rr-empty .rr-cross {
  height: 8em;
}

/* A6: the name gets its own row so long names stay readable */
.receipt-page.rxpad .erh-patient-a6 {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.2rem 1rem;
}

.receipt-page.rxpad .erh-patient-a6 .erh-patient-name {
  flex-basis: 100%;
}

.receipt-page.rxpad .erh-appt {
  background: transparent !important;
  border-width: 1.5px;
}

.receipt-page.rxpad .erh-contact {
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.15rem 1rem;
  padding: 0.4rem 0.1rem;
  border-radius: 0;
  border-top: 3px double var(--rx);
  border-bottom: 3px double var(--rx);
  background: transparent !important;
  color: #111 !important;
}

.receipt-page.rxpad .er-title,
.receipt-page.rxpad .rr-title {
  border-left: 0 !important;
  border-bottom: 1px solid #111;
  padding-left: 0;
  padding-bottom: 0.15em;
  color: #111;
}

.receipt-page.rxpad .er-table {
  border: 0;
  border-radius: 0;
}

.receipt-page.rxpad .er-table thead {
  background: transparent !important;
}

.receipt-page.rxpad .er-table th {
  border-bottom: 0;
  color: #666;
}

.receipt-page.rxpad .er-table td {
  border-top: 1px solid #e2e2e2;
  font-weight: 800;
}

.receipt-page.rxpad .er-table tbody tr:first-child td {
  border-bottom: 0;
}

.receipt-page.rxpad .er-table td + td,
.receipt-page.rxpad .er-table th + th {
  border-left: 0;
}

.receipt-page.rxpad .er-eye {
  color: var(--rx);
}

.receipt-page.rxpad .rr-net {
  background: transparent !important;
  border: 1.5px solid var(--rx);
  border-radius: 6px;
}

/* A6 rows are short, so the A4/A5 radius looks like a pill there */
.receipt-page.rxpad.a6-size .rr-net {
  border-radius: 2px;
}

.receipt-page.rxpad .er-notes,
.receipt-page.rxpad .rr-notes {
  border: 1px dashed #999;
  border-radius: 0;
  background: transparent;
}

/* Rx Pad A6 prints larger text than the classic design; the bottom padding keeps the
   Thecky logo and QR clear of the printer's unprintable margin */
.receipt-page.rxpad.a6-size {
  font-size: 10.5px;
  padding-bottom: 5mm;
}

.receipt-page.rxpad .er-a6 .er-title,
.receipt-page.rxpad .er-a6-tight .er-title {
  font-size: 12px;
}

.receipt-page.rxpad .er-a6 .er-table th,
.receipt-page.rxpad .er-a6-tight .er-table th {
  font-size: 10px;
}

.receipt-page.rxpad .er-a6 .er-table td,
.receipt-page.rxpad .er-a6-tight .er-table td {
  font-size: 14px;
  height: 21px;
  padding: 1px 2px;
  line-height: 1.1;
}

/* A6 with Contact Lenses / Prism: denser rows so the whole exam fits one page */
.receipt-page.rxpad .er-a6-tight {
  gap: 4px;
  margin-top: 0.25rem;
}

.receipt-page.rxpad.a6-size .erh-band {
  padding-top: 0.6rem;
}

.receipt-page.rxpad .a6-patient-gap {
  margin-top: 0.15rem;
  padding-top: 0.15rem;
}

.receipt-page.rxpad .erh-patient-a6 .erh-patient-name .erh-label {
  display: none;
}

.receipt-page.rxpad .er-a6-tight .er-row {
  gap: 4px;
}

.receipt-page.rxpad .er-a6-tight .er-title {
  font-size: 11px;
  margin-bottom: 1px;
}

.receipt-page.rxpad .er-a6-tight .er-table th {
  font-size: 9px;
  padding: 1px 2px;
}

.receipt-page.rxpad .er-a6-tight .er-table td {
  font-size: 13px;
  height: 17px;
  padding: 0 2px;
  line-height: 1.05;
}
</style>
